import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UserStatus, BYPASS_REDIRECT_PATHS } from './types';
import { RootState } from '../../core/store';
import {
  EnrollmentStatus,
  EnrollmentSteps,
  UserStatus as Status,
} from '../../core/constant/constant';
import calculateAge from '../../core/constant/utils/helper';
import moment from 'moment';
import { setEnrollmentStatus } from '../../core/store/slices/enrollment.slice';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../features/auth/hooks/useAuth';

export const useUserStatus = () => {
  // State to store the user's enrollment status
  const [status, setStatus] = useState<UserStatus | null>(null);
  // State to track if data is still loading
  const [loading, setLoading] = useState(true);

  // Redux selectors to get different parts of the user data from store
  const basicInfo = useSelector((state: RootState) => state.basicInfo);
  const preferenceInformation = useSelector((state: RootState) => state.preferenceInformation);
  const memberSubscription = useSelector((state: RootState) => state.memberSubscription);
  const availability = useSelector((state: RootState) => state.availability);
  const emergencyContact = useSelector((state: RootState) => state.emergencyContact);
  const personalDetails = useSelector((state: RootState) => state.personalDetails);
  const enrollmentStatus = useSelector((state: RootState) => state.enrollment.enrollmentStep);
  const dispatch = useDispatch();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  // Check if current path should bypass redirect logic
  const shouldBypassRedirect = BYPASS_REDIRECT_PATHS.includes(location.pathname);

  useEffect(() => {
    // Don't compute status if user is not authenticated
    // Let ProtectedRoute handle redirecting to /auth
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    // Format date of birth and calculate user's age
    const date = moment(personalDetails?.dob).format('MM-DD-YYYY');
    const age = calculateAge(date) || 0;

    // Array of status values indicating non-active coaching states
    const isNonActiveCoaching = [
      Status.AWAY,
      Status.EXITED,
      Status.EXITED_BY_REQUEST,
      Status.EXITED_BY_STOP,
      Status.IDLE_COACHING,
    ];
    // Array of status values indicating budget limitation states
    const isBudgedLimit = [
      Status.AWAY_BUDGET_LIMIT,
      Status.AWAY_TEXT_LIMIT,
      ...isNonActiveCoaching,
    ];
    // Check if user is logging in for the first time or just signed up
    const isFirstTimeLogin =
      basicInfo.isFirstTimeLogin ||
      basicInfo.currentStatus === Status.SIGNED_UP ||
      basicInfo.currentStatus === Status.COMMUNITY_MEMBER;
    // Check if personal details are complete (first name, last name, DOB, address)
    const isPersonalDetailsComplete =
      Boolean(personalDetails.firstName) &&
      Boolean(personalDetails.lastName) &&
      Boolean(personalDetails.dob) &&
      Boolean(personalDetails.address);
    // Check if membership subscription is active and first payment processed
    const isMemberSubscriptionActive =
      Boolean(memberSubscription) &&
      memberSubscription?.status === EnrollmentStatus.MEMBERSHIP_ACTIVE;
    // Check if basic information is complete based on primary partner name
    const isBasicInfoComplete = Boolean(basicInfo.primaryPartnerName) || isMemberSubscriptionActive;
    // Check if availability information is complete
    const isAvailabilityComplete =
      availability.shiftDays.length !== 0 && Boolean(availability.shiftTime);
    // Check if preference information is complete based on "hope to gain" field
    const isPreferenceInformationComplete = Boolean(preferenceInformation.hopeToGain);
    // Simplify emergency contact check for users over 18
    const isMinor = age < 18;
    const isEmergencyContactComplete = isMinor
      ? Boolean(emergencyContact.contactName) && Boolean(emergencyContact.contactPhone)
      : true;
    // Check if current enrollment step is emergency contact
    const isCurrentStepEmergency =
      basicInfo.userEnrollment?.currentStep === EnrollmentSteps.ENROLLMENT_EMERGENCY_CONTACT &&
      basicInfo.userEnrollment.completed;
    // Check if first enrollment step is complete (basic info + personal details)
    const isStepOneComplete = isBasicInfoComplete && isPersonalDetailsComplete;
    // Check if second enrollment step is complete (preferences + availability + emergency contact)
    const isStepTwoComplete = isAvailabilityComplete && isEmergencyContactComplete;
    // Check if all required data is available for enrollment completion
    const isAllRequiredDataAvailable =
      isStepOneComplete && isStepTwoComplete && isMemberSubscriptionActive;
    // Check if user qualifies for full dashboard access
    const isFullDashboard =
      isAllRequiredDataAvailable &&
      isCurrentStepEmergency &&
      basicInfo.currentStatus !== Status.PENDING_MATCHING;

    const budgetLimitChecked = isBudgedLimit.includes(basicInfo.currentStatus as Status);

    // Exit early if basic user data hasn't loaded yet
    if (!basicInfo || basicInfo._id === undefined) {
      setLoading(true);
      return;
    }
    // Variable to hold the determined user enrollment status
    let userStatus: UserStatus;

    // Partners that skip Stripe subscription — route to /home without requiring active subscription
    const EXEMPT_PARTNER_PATTERNS = [/\bthe loveland foundation\b/i, /\btlf\b/i, /\beditor trial\b/i];
    const isExemptPartner = EXEMPT_PARTNER_PATTERNS.some((re) => re.test((basicInfo.primaryPartnerName || '').trim()));

    // CONDITION: Exempt partner + ACTIVE_COACHING → Go to HOME page directly
    if (
      isExemptPartner &&
      basicInfo.currentStatus === Status.ACTIVE_COACHING
    ) {
      userStatus = EnrollmentStatus.FULLY_ENROLLED_ACTIVE;
    }
    // CONDITION: ACTIVE_COACHING who completed signup (set via Wakanda after full signup)
    // User has: active subscription + personal details + (availability OR completed enrollment) → Go to HOME page
    // NOTE: We check either isAvailabilityComplete OR userEnrollment.completed because
    // some users may have completed enrollment before availability was properly saved to Redux
    else if (
      basicInfo.currentStatus === Status.ACTIVE_COACHING &&
      isMemberSubscriptionActive &&
      isPersonalDetailsComplete &&
      (isAvailabilityComplete || basicInfo.userEnrollment?.completed) &&
      !shouldBypassRedirect
    ) {
      userStatus = EnrollmentStatus.FULLY_ENROLLED_ACTIVE;
    }
    // ACTIVE_COACHING users on bypass paths (e.g. /billing, /support, /subscriptions)
    else if (
      basicInfo.currentStatus === Status.ACTIVE_COACHING &&
      isMemberSubscriptionActive &&
      isPersonalDetailsComplete &&
      (isAvailabilityComplete || basicInfo.userEnrollment?.completed) &&
      shouldBypassRedirect
    ) {
      userStatus = EnrollmentStatus.FULLY_ENROLLED_ACTIVE;
    }
    // CONDITION: COMMUNITY_MEMBER or SIGNED_UP - check enrollment completion first
    // If enrollment is NOT completed, MUST go to /enrollment first
    else if (
      (basicInfo.currentStatus === Status.COMMUNITY_MEMBER ||
        basicInfo.currentStatus === Status.SIGNED_UP) &&
      isStepOneComplete &&
      !basicInfo.userEnrollment?.completed &&
      !shouldBypassRedirect
    ) {
      userStatus = EnrollmentStatus.INCOMPLETE_ENROLLMENT;
      dispatch(setEnrollmentStatus(EnrollmentSteps.ENROLLMENT_COMPLETE));
    }
    // CONDITION: COMMUNITY_MEMBER or SIGNED_UP with completed enrollment → /application-received
    else if (
      (basicInfo.currentStatus === Status.COMMUNITY_MEMBER ||
        basicInfo.currentStatus === Status.SIGNED_UP) &&
      isStepOneComplete &&
      basicInfo.userEnrollment?.completed &&
      !shouldBypassRedirect
    ) {
      userStatus = EnrollmentStatus.PENDING_MATCHING;
    }
    // CONDITION: COMMUNITY_MEMBER or SIGNED_UP on bypass path (like /application-received) - just set status, no redirect
    else if (
      (basicInfo.currentStatus === Status.COMMUNITY_MEMBER ||
        basicInfo.currentStatus === Status.SIGNED_UP) &&
      isStepOneComplete &&
      shouldBypassRedirect
    ) {
      // User is on a bypass path (e.g., /application-received after payment)
      // Set status but don't navigate - let them stay on the current page
      userStatus = EnrollmentStatus.PENDING_MATCHING;
    }
    // Condition: User is PENDING_MATCHING with completed enrollment (from Wakanda flow)
    // These users may have skipped Step 2 (additional info) but still need to stay on /application-received
    else if (
      basicInfo.currentStatus === Status.PENDING_MATCHING &&
      isMemberSubscriptionActive &&
      isStepOneComplete &&
      basicInfo.userEnrollment?.completed
    ) {
      userStatus = EnrollmentStatus.PENDING_MATCHING;
    }
    // // Condition: User has all required data and is at emergency contact step
    // else if (isAllRequiredDataAvailable && basicInfo.userEnrollment?.currentStep === EnrollmentSteps.ENROLLMENT_EMERGENCY_CONTACT) {
    //   userStatus = EnrollmentStatus.FULLY_ENROLLED_ACTIVE;
    // }
    // Condition: User has a non-active coaching status but has all required data
    else if (
      isNonActiveCoaching.includes(basicInfo.currentStatus as Status) &&
      isAllRequiredDataAvailable &&
      isCurrentStepEmergency
    ) {
      userStatus = EnrollmentStatus.FULLY_ENROLLED_ACTIVE;
    }
    // Condition: User has reached budget limitation
    else if (
      isBudgedLimit.includes(basicInfo.currentStatus as Status) &&
      isMemberSubscriptionActive &&
      isCurrentStepEmergency
    ) {
      userStatus = EnrollmentStatus.FULLY_ENROLLED_ACTIVE;
    }
    // Condition: User is new or just signed up and hasn't activated membership
    else if (isFirstTimeLogin && !basicInfo.userEnrollment) {
      userStatus = EnrollmentStatus.FIRST_TIME_LOGIN;
    }
    // Condition: First enrollment step not complete or user just signed up
    // Note: COMMUNITY_MEMBER with completed enrollment should NOT enter this block
    else if (
      !isStepOneComplete ||
      basicInfo.currentStatus === Status.SIGNED_UP ||
      (basicInfo.currentStatus === Status.COMMUNITY_MEMBER &&
        !basicInfo.userEnrollment?.completed) ||
      budgetLimitChecked ||
      !isMemberSubscriptionActive
    ) {
      // Sub-condition: Basic info not complete - set status to contact info
      if (!isBasicInfoComplete) {
        userStatus = EnrollmentStatus.INCOMPLETE_ENROLLMENT;
        dispatch(setEnrollmentStatus(EnrollmentSteps.ENROLLMENT_CONTACT_INFO));
      }
      // Sub-condition: Personal details not complete - set status to personal details
      else if (!isPersonalDetailsComplete) {
        userStatus = EnrollmentStatus.INCOMPLETE_ENROLLMENT;
        dispatch(setEnrollmentStatus(EnrollmentSteps.ENROLLMENT_PERSONAL_DETAILS));
      }
      // Sub-condition: Both basic info and personal details complete, ready for completion
      // Route to enrollment page when:
      // 1. Subscription is not active (needs payment), OR
      // 2. Status is ACTIVE_COACHING, COMMUNITY_MEMBER, or SIGNED_UP and enrollment is not yet complete
      else if (
        isBasicInfoComplete &&
        isPersonalDetailsComplete &&
        (memberSubscription.status !== EnrollmentStatus.MEMBERSHIP_ACTIVE ||
          ((basicInfo.currentStatus === Status.ACTIVE_COACHING ||
            basicInfo.currentStatus === Status.COMMUNITY_MEMBER ||
            basicInfo.currentStatus === Status.SIGNED_UP) &&
            !basicInfo.userEnrollment?.completed))
      ) {
        userStatus = EnrollmentStatus.INCOMPLETE_ENROLLMENT;
        dispatch(setEnrollmentStatus(EnrollmentSteps.ENROLLMENT_COMPLETE));
      }
      // Sub-condition: Default to additional information and preference step
      else {
        dispatch(setEnrollmentStatus(EnrollmentSteps.ENROLLMENT_PREFERENCE_INFORMATION));
        if (!isStepTwoComplete || isMemberSubscriptionActive || budgetLimitChecked) {
          userStatus = EnrollmentStatus.ADDITIONAL_INFORMATION;
          // Sub-condition: Preference information not complete
          if (
            !isPreferenceInformationComplete ||
            basicInfo.userEnrollment?.toStep === EnrollmentSteps.ENROLLMENT_PREFERENCE_INFORMATION
          ) {
            dispatch(setEnrollmentStatus(EnrollmentSteps.ENROLLMENT_PREFERENCE_INFORMATION));
          }
          // Sub-condition: Availability information not complete
          else if (
            !isAvailabilityComplete ||
            basicInfo.userEnrollment?.toStep === EnrollmentSteps.ENROLLMENT_AVAILABILITY
          ) {
            dispatch(setEnrollmentStatus(EnrollmentSteps.ENROLLMENT_AVAILABILITY));
          }
          // Sub-condition: Emergency contact and availability complete
          else if (
            (isEmergencyContactComplete && isAvailabilityComplete) ||
            basicInfo.userEnrollment?.toStep === EnrollmentSteps.ENROLLMENT_EMERGENCY_CONTACT
          ) {
            dispatch(setEnrollmentStatus(EnrollmentSteps.ENROLLMENT_EMERGENCY_CONTACT));
          }
        } else {
          userStatus = EnrollmentStatus.ADDITIONAL_INFORMATION;
          dispatch(setEnrollmentStatus(EnrollmentSteps.ENROLLMENT_PREFERENCE_INFORMATION));
        }
      }
    }
    // Condition: Second enrollment step not complete or membership is active
    else if (!isStepTwoComplete || isMemberSubscriptionActive || budgetLimitChecked) {
      userStatus = EnrollmentStatus.ADDITIONAL_INFORMATION;
      // Sub-condition: Preference information not complete
      if (
        !isPreferenceInformationComplete &&
        basicInfo.userEnrollment?.currentStep === EnrollmentSteps.ENROLLMENT_PREFERENCE_INFORMATION
      ) {
        dispatch(setEnrollmentStatus(EnrollmentSteps.ENROLLMENT_PREFERENCE_INFORMATION));
      }
      // Sub-condition: Availability information not complete
      else if (
        !isAvailabilityComplete ||
        basicInfo.userEnrollment?.currentStep === EnrollmentSteps.ENROLLMENT_AVAILABILITY
      ) {
        dispatch(setEnrollmentStatus(EnrollmentSteps.ENROLLMENT_AVAILABILITY));
      }
      // Sub-condition: Emergency contact and availability complete
      else if (isEmergencyContactComplete && isAvailabilityComplete) {
        dispatch(setEnrollmentStatus(EnrollmentSteps.ENROLLMENT_EMERGENCY_CONTACT));
      }
      // Sub-condition: Default to pending matching
      else {
        userStatus = EnrollmentStatus.PENDING_MATCHING;
      }
    }
    // Condition: User enrollment data missing - first time login
    else if (!basicInfo.userEnrollment) {
      userStatus = EnrollmentStatus.FIRST_TIME_LOGIN;
    }
    // Default condition: Set to pending matching
    else {
      userStatus = EnrollmentStatus.PENDING_MATCHING;
    }

    // Update the status state and set loading to false
    setStatus(userStatus);
    setLoading(false);
  }, [
    // Dependencies that trigger re-evaluation when they change
    basicInfo.isFirstTimeLogin,
    basicInfo.currentStatus,
    basicInfo.isInOnboarding,
    basicInfo.userEnrollment?.completed,
    memberSubscription?.status,
    memberSubscription?.messagesRemaining,
    memberSubscription?.firstPaymentId,
    preferenceInformation.hopeToGain,
    emergencyContact.contactName,
    availability.shiftDays,
    basicInfo.isCommunityMemberDialog,
    personalDetails.dob,
    shouldBypassRedirect,
    location.pathname,
    isAuthenticated,
  ]);

  // Return the current status and loading state
  return { status, loading };
};
